import {AG_GRID_COMPATIBILITY, JustTooltipFormatter, ProgressBarFormatter, TableSorting, useFormatters} from '@wallaby-ui/document';
import {get} from 'lodash';
import {Column} from 'material-table';
import * as React from 'react';
import {useMemo} from 'react';

import {getReportTableLink} from './HospitalReportTable';
import {InterventionOverviewReportFilter, ReportType, RowTypeWithTotal} from './types';

export const useReportTableColumns = (
  defaultSort: TableSorting,
  reportFilters: InterventionOverviewReportFilter,
  reportType: ReportType
): Column<RowTypeWithTotal>[] => {
  const EMPTY = '-';
  const {t, formatFloat} = useFormatters();
  let organizationColumnName = 'hospital.network.report.table.column.hospital';
  if (reportType === ReportType.DEPARTMENT) {
    if (reportFilters.organizationLevelType === 'PARENT_DEPARTMENT') {
      organizationColumnName = 'hospital.report.filter.departmentdetail.department';
    } else {
      organizationColumnName = 'hospital.report.filter.departmentdetail.unit';
    }
  }

  return useMemo(() => {
    const columns: Column<RowTypeWithTotal>[] = [
      {
        field: 'organizationLabel',
        title: t(organizationColumnName),
        render: (data) => (
          <JustTooltipFormatter
            {...AG_GRID_COMPATIBILITY}
            value={data.organizationLabel || EMPTY}
            tooltipOverride={data.organizationLabel}
            bold={data.isTotalRow}
            route={getReportTableLink(data, reportFilters, reportType)}
          />
        ),
        sorting: true,
      },
      {
        field: 'measureCount',
        title: t('hospital.network.report.table.column.total'),
        render: (data) => {
          const measureCount = formatFloat(data.measureCount);
          return (
            <JustTooltipFormatter
              {...AG_GRID_COMPATIBILITY}
              value={measureCount ?? EMPTY}
              tooltipOverride={measureCount}
              bold={data.isTotalRow}
            />
          );
        },
        width: '122px',
        sorting: true,
      },
      {
        field: 'measureCountToHospCountPct',
        title: t('hospital.network.report.table.column.measureCountToHospCountPct'),
        render: (data) => {
          const percent = data.measureCountToHospCountPct;
          const percentString = `${formatFloat(percent)} %`;
          return (
            <ProgressBarFormatter
              {...AG_GRID_COMPATIBILITY}
              value={percentString ?? EMPTY}
              tooltipOverride={percentString}
              bold={data.isTotalRow}
              progressPercent={percent}
              style={{maxWidth: '150px'}}
            />
          );
        },
        sorting: true,
      },
      {
        field: 'hospCount',
        title: t('hospital.network.report.table.column.hospCount'),
        render: (data) => {
          const hospCountString = formatFloat(data.hospCount);
          return (
            <JustTooltipFormatter
              {...AG_GRID_COMPATIBILITY}
              value={hospCountString ?? EMPTY}
              tooltipOverride={hospCountString}
              bold={data.isTotalRow}
            />
          );
        },
        sorting: true,
      },
      {
        field: 'measureCountPer1000HospDays',
        title: t('hospital.network.report.table.column.measureCountPer1000HospDays'),
        render: (data) => {
          const careDaysCountString = formatFloat(data.measureCountPer1000HospDays);
          return (
            <JustTooltipFormatter
              {...AG_GRID_COMPATIBILITY}
              value={careDaysCountString ?? EMPTY}
              tooltipOverride={careDaysCountString}
              bold={data.isTotalRow}
            />
          );
        },
        sorting: true,
      },
      {
        field: 'hospDaysCount',
        title: t('hospital.network.report.table.column.hospDaysCount'),
        render: (data) => {
          const numberOfCareDaysString = formatFloat(data.hospDaysCount);
          return (
            <JustTooltipFormatter
              {...AG_GRID_COMPATIBILITY}
              value={numberOfCareDaysString ?? EMPTY}
              tooltipOverride={numberOfCareDaysString}
              bold={data.isTotalRow}
            />
          );
        },
        sorting: true,
      },
      {
        field: 'uncheckedHospCount',
        title: t('hospital.network.report.table.column.uncheckedHospCount'),
        render: (data) => {
          const uncheckedHospCount = formatFloat(data.uncheckedHospCount);
          return (
            <JustTooltipFormatter
              {...AG_GRID_COMPATIBILITY}
              value={uncheckedHospCount ?? EMPTY}
              tooltipOverride={uncheckedHospCount}
              bold={data.isTotalRow}
            />
          );
        },
        sorting: true,
      },
      {
        field: 'uncheckedHighRiskHospCount',
        title: t('hospital.network.report.table.column.uncheckedHighRiskHospCount'),
        render: (data) => {
          const uncheckedHighRiskHospCount = formatFloat(data.uncheckedHighRiskHospCount);
          return (
            <JustTooltipFormatter
              {...AG_GRID_COMPATIBILITY}
              value={uncheckedHighRiskHospCount ?? EMPTY}
              tooltipOverride={uncheckedHighRiskHospCount}
              bold={data.isTotalRow}
            />
          );
        },
        sorting: true,
        tooltip: t('hospital.network.report.table.column.uncheckedHighRiskHospCount.tooltip'),
      },
    ];

    return columns.map((column) => ({
      ...column,
      sorting: column.sorting ?? false,
      customSort: (row1, row2, _type) => {
        const isSortAscending = defaultSort.direction === 'asc';
        if (row1.isTotalRow) return isSortAscending ? -1 : 1;
        if (row2.isTotalRow) return isSortAscending ? 1 : -1;

        const field = column.field ?? '';
        const data1 = get(row1, field);
        const data2 = get(row2, field);
        return +(data1 > data2) - +(data1 < data2);
      },
      ...(column.field === defaultSort.field ? {defaultSort: defaultSort.direction ?? undefined} : {}),
    }));
  }, [t, organizationColumnName, reportFilters, reportType, formatFloat, defaultSort.field, defaultSort.direction]);
};
